import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/buttons';
import { CurrencyCode } from '@/helpers/currency';
import { Modals } from '@/helpers/modals';
import { useExchangeAmount } from '@/modules/common';
import { useGetCommonStore, useModalsStore } from '@/store';

const MIN_BALANCE_EUR = 10;

interface ICtaButtonProps {
  isNotStarted: boolean;
  namespace: string;
  alwaysDeposit?: boolean;
}

export const CtaButton = ({
  isNotStarted,
  namespace,
  alwaysDeposit,
}: ICtaButtonProps) => {
  const { t } = useTranslation(namespace);
  const open = useModalsStore.use.open();

  const useCommonStore = useGetCommonStore();
  const isAuthenticated = useCommonStore.use.isAuthenticated();
  const currentWallet = useCommonStore.use.currentWallet();

  // Convert 10 EUR to user's currency to compare with balance
  const { exchangeAmount: minBalanceInUserCurrency } = useExchangeAmount({
    amount: MIN_BALANCE_EUR,
    inputCurrency: CurrencyCode.EUR,
    currencyType: 'fiat',
  });

  const hasLowBalance = useMemo(() => {
    if (!currentWallet) return false;
    return currentWallet.amountCents < minBalanceInUserCurrency;
  }, [currentWallet, minBalanceInUserCurrency]);

  const handleOpenRegistration = () => {
    open(Modals.Registration);
  };

  const handleOpenDeposit = () => {
    open(Modals.Deposit);
  };

  if (!isAuthenticated) {
    return (
      <Button
        className="regButton"
        onClick={handleOpenRegistration}
        primary
        wide
      >
        {t('cta.reg')}
      </Button>
    );
  }

  if (alwaysDeposit) {
    return (
      <Button className="regButton" onClick={handleOpenDeposit} primary wide>
        {isNotStarted ? t('cta.joinNow') : t('cta.play')}
      </Button>
    );
  }

  if (hasLowBalance) {
    return (
      <Button className="regButton" onClick={handleOpenDeposit} primary wide>
        {t('cta.deposit')}
      </Button>
    );
  }

  return (
    <Button className="regButton" primary to="/" wide>
      {isNotStarted ? t('cta.joinNow') : t('cta.play')}
    </Button>
  );
};
